import { useTranslation } from 'react-i18next';
import { Box, Field, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';
import { InputField } from '@themes/aegix';
import { useFormContext } from 'react-hook-form';
import { UserFormInitialValues } from '../../types/UserFormInitialValue';
import TransferList from './TransferList';
import { useEffect, useState } from 'react';
import { useGroups, useSites, useUser } from '../../hooks/queries';
import { useUserSchoolMembership } from '../../hooks/queries/useSchool';
import UserSitesSection from '../users/UserSitesSection';
import { AdminSubscriptionsEditor } from '../users/AdminSubscriptionsEditor';
import { useHasPermission } from '@aegix/auth-permissions';
import { Button as ButtonComponent, ToggleButton } from '@themes/aegix';

interface Option {
  label: string;
  value: string;
}
interface UserFormInterface {
  setActionDialog: (params: { action: string; open: boolean }) => void;
}
const UsersForm: React.FC<UserFormInterface> = (props) => {
  const { t } = useTranslation();
  const { setActionDialog } = props;
  // Gates the admin-on-behalf Subscriptions section below.
  const canManageSubscriptions = useHasPermission(
    'accounts.can_manage_subscription',
  );
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<UserFormInitialValues>();
  const values = watch();
  const [schoolOptions, setSchoolOptions] = useState<Option[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<
    { label: string; value: string }[]
  >([]); // React Query - fetch all groups once (they rarely change)
  const { data, isLoading } = useGroups({
    limit: 10,
  });
  const { data: SchoolData } = useSites({
    limit: 10,
  });
  // Fetch groups once the query finishes
  const { data: userSites } = useUserSchoolMembership({
    baseuser: Number(values?.id),
    limit: 100,
    offset: 0,
  });
  const [isAddSite, setIsAddSite] = useState(false);
  const [userSelectSiteDetails, setUserSelectSiteDetails] = useState<Option[]>(
    [],
  );
  const { data: userDetails } = useUser(Number(values?.id));
  useEffect(() => {
    if (userSites?.results?.length) {
      setValue(
        'schools',
        userSites.results.map((item) => item.school),
      );
    }
  }, [userSites, setValue]);

  useEffect(() => {
    if (data?.results) {
      const groupOption: Option[] = data.results.map(
        (list: { name: string; id: string | number }) => ({
          label: list.name,
          value: list.id + '',
        }),
      );
      setPermissionOptions(groupOption);
    }
  }, [data]);
  useEffect(() => {
    if (SchoolData?.results) {
      const SchoolOption = SchoolData.results.map(
        (list: { name: string; id: string | number }) => ({
          label: list.name,
          value: String(list.id),
        }),
      );
      setSchoolOptions(SchoolOption);
    }
  }, [SchoolData]);
  useEffect(() => {
    if (
      userDetails &&
      Object.keys(userDetails).length > 0 &&
      userDetails.groups
    ) {
      setValue('groups', userDetails.groups.map(String));
    }
  }, [userDetails]);
  // handle select data in  select input
  const handleSelectSiteChange = (selectedOption: string) => {
    const selectedId = String(selectedOption);
    // Find selected object
    const findSelectedObject = schoolOptions?.filter(
      (item: { value: string }) => item.value === selectedId,
    );
    // Remove selected object from list
    const removeSelectedObject = schoolOptions?.filter(
      (item: { value: string }) => item.value !== selectedId,
    );
    setSchoolOptions(removeSelectedObject);
    // Store selected site details
    setUserSelectSiteDetails((prev) => [...prev, ...findSelectedObject]);
    //  Add selected value to schools
    const currentSchools = watch('schools') || [];
    setValue('schools', [...currentSchools, Number(selectedId)]);
    setIsAddSite(false);
  };
  const handleSiteDelete = (data: Option) => {
    // Remove from selected site details (UI)
    setUserSelectSiteDetails((prev) =>
      prev.filter((item) => item.value !== data.value),
    );

    // Add back to available options
    setSchoolOptions((prev) => [...prev, data]);

    //  Remove from schools
    const currentSchools = watch('schools') || [];
    setValue(
      'schools',
      currentSchools.filter((id) => id !== Number(data.value)),
    );
  };
  useEffect(() => {
    if (!userSites?.results?.length && !SchoolData?.results.length) return;

    const unmatchedSchool = SchoolData?.results?.filter(
      (item1: { id: number }) =>
        !userSites?.results?.some(
          (item2: { school: number }) => item2.school === item1.id,
        ),
    );
    const unmatchedSchoolFilter: Option[] = unmatchedSchool
      ? unmatchedSchool.map((item: { name: string; id: string | number }) => ({
        label: item.name,
        value: String(item.id),
      }))
      : [];
    const matchingItems = SchoolData?.results?.reduce(
      (acc: Option[], currentItem: { id: number; name: string }) => {
        const matchingItem = userSites?.results?.find(
          (item: { school: number }) => item.school === currentItem.id,
        );
        if (matchingItem) {
          acc.push({ value: currentItem.id + '', label: currentItem.name });
        }
        return acc;
      },
      [],
    );
    const filteredArray = unmatchedSchoolFilter
      ?.sort((a: { label: string }, b: { label: string }) => {
        if (!b.label) return -1; // Discard null or undefined by treating it as smaller
        if (!a.label) return 1; // Discard null or undefined by treating it as larger
        return a.label.localeCompare(b.label);
      })
      ?.filter((data1: { value: string | number }) => {
        return !matchingItems?.some(
          (data2: { value: string | number }) => data1.value === data2.value,
        );
      });
    setSchoolOptions(filteredArray);
    setUserSelectSiteDetails(matchingItems || []);
  }, [SchoolData?.results, userSites?.results]);

  return (
    <VStack gap={4} align="stretch">
      <Field.Root invalid={!!errors.email}>
        <Field.Label htmlFor="user-email">{t('common.email', 'Email Address')}</Field.Label>
        <InputField
          id="user-email"
          name="email"
          placeholder={t('common.email', 'Email Address')}
          value={values.email}
          onChange={(e) => setValue('email', e.target.value)}
          data-e2e="user-email-input"
          aria-describedby={errors.email ? 'user-email-error' : undefined}
        />
        {errors.email && (
          <Field.ErrorText id="user-email-error" data-e2e="user-email-error">
            {errors.email.message ? t(errors.email.message) : null}
          </Field.ErrorText>
        )}
      </Field.Root>
      <Field.Root invalid={!!errors.first_name}>
        <Field.Label htmlFor="user-first-name">{t('common.firstName', 'First name')}</Field.Label>
        <InputField
          id="user-first-name"
          name="first_name"
          placeholder={t('common.firstName', 'First name')}
          value={values.first_name}
          onChange={(e) => setValue('first_name', e.target.value)}
          data-e2e="user-first-name-input"
          aria-describedby={
            errors.first_name ? 'user-first-name-error' : undefined
          }
        />
        {errors.first_name && (
          <Field.ErrorText
            id="user-first-name-error"
            data-e2e="user-firstname-error"
          >
            {errors.first_name.message ? t(errors.first_name.message) : null}
          </Field.ErrorText>
        )}
      </Field.Root>
      <Field.Root invalid={!!errors.last_name}>
        <Field.Label htmlFor="user-last-name">{t('common.lastName', 'Last name')}</Field.Label>
        <InputField
          id="user-last-name"
          name="last_name"
          placeholder={t('common.lastName', 'Last name')}
          value={values.last_name}
          onChange={(e) => setValue('last_name', e.target.value)}
          data-e2e="user-last-name-input"
          aria-describedby={
            errors.last_name ? 'user-last-name-error' : undefined
          }
        />
        {errors.last_name && (
          <Field.ErrorText
            id="user-last-name-error"
            data-e2e="user-lastname-error"
          >
            {errors.last_name.message ? t(errors.last_name.message) : null}
          </Field.ErrorText>
        )}
      </Field.Root>
      {!values.id && (
        <Field.Root invalid={!!errors.password}>
          <Field.Label htmlFor="user-password">{t('users.password', 'Password')}</Field.Label>
          <InputField
            id="user-password"
            name="password"
            placeholder={t('users.password', 'Password')}
            type="password"
            value={values.password}
            onChange={(e) => setValue('password', e.target.value)}
            data-e2e="user-password-input"
            aria-describedby={
              errors.password ? 'user-password-error' : undefined
            }
          />
          {errors.password && (
            <Field.ErrorText
              id="user-password-error"
              data-e2e="user-password-error"
            >
              {errors.password.message ? t(errors.password.message) : null}
            </Field.ErrorText>
          )}
        </Field.Root>
      )}
      {!values.id && (
        <Field.Root invalid={!!errors.confirm_password}>
          <Field.Label htmlFor="user-confirm-password">
            {t('users.confirmPassword', 'Confirm Password')}
          </Field.Label>
          <InputField
            id="user-confirm-password"
            name="confirm_password"
            placeholder={t('users.confirmPassword', 'Confirm Password')}
            type="password"
            value={values.confirm_password}
            onChange={(e) => setValue('confirm_password', e.target.value)}
            data-e2e="user-confirm-password-input"
            aria-describedby={
              errors.confirm_password
                ? 'user-confirm-password-error'
                : undefined
            }
          />
          {errors.confirm_password && (
            <Field.ErrorText
              id="user-confirm-password-error"
              data-e2e="user-confirmpassword-error"
            >
              {errors.confirm_password.message ? t(errors.confirm_password.message) : null}
            </Field.ErrorText>
          )}
        </Field.Root>
      )}
      {values.id && (
        <SimpleGrid columns={2} gap={4}>
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={() =>
              setActionDialog({
                action: 'reset',
                open: true,
              })
            }
            data-e2e="reset-password-button"
          >
            {t('users.resetPassword', 'Reset Password')}
          </ButtonComponent>
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={() =>
              setActionDialog({
                action: 'activity',
                open: true,
              })
            }
            data-e2e="activity-button"
          >
            {t('users.activity', 'Activity')}
          </ButtonComponent>
        </SimpleGrid>
      )}

      <Stack direction="row" mt={2} mb={2}>
        <ToggleButton
          title={t('users.disableSso', 'Disable SSO')}
          e2eTitle={'disable_sso'}
          checked={values.disable_sso ?? false}
          onCheckedChange={() => {
            setValue('disable_sso', !values.disable_sso);
          }}
        />
      </Stack>

      <VStack gap={1.5} align="stretch">
        {!isLoading && (
          <TransferList
            options={permissionOptions}
            value={values.groups}
            onChange={(val) => setValue('groups', val)}
          />
        )}
      </VStack>

      <UserSitesSection
        sites={userSelectSiteDetails || []}
        availableSites={schoolOptions || []}
        isAddMode={isAddSite}
        onAddModeChange={setIsAddSite}
        onSiteSelect={handleSelectSiteChange}
        onSiteDelete={handleSiteDelete}
        canEdit={true}
      />

      {/* Admin-on-behalf subscription management.
          Edit mode only (needs a persisted user id); gated on the same
          permission the server checks for on-behalf access. The list is
          server-intersected with the acting admin's sites. */}
      {values?.id != null && canManageSubscriptions && (
        <Box mt={2}>
          <Text fontSize="md" fontWeight={600} color="text.primary" mb={2}>
            {t('users.subscriptions', 'Subscriptions')}
          </Text>
          <AdminSubscriptionsEditor userId={Number(values.id)} />
        </Box>
      )}
    </VStack>
  );
};
export default UsersForm;
